import * as Sentry from '@sentry/browser';
import { sendWebexEvent, setWebExUserAttributes } from '@klv/context/experimentation/utils';
import type { IInitialFormValues, TFormType } from '../types';
import { hasStorageAvailable } from '@klv/shared/utils/storage';

const trackSignupSubmission = (url: string) => {
  fetch('/request-mkt/process/signup-form-success', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      url,
    }),
  }).catch((error) => {
    console.error('Error calling signup form success tracker:', error);
  });
};

const handleSuccess = (values: IInitialFormValues, form: TFormType) => {
  window?.heap?.track?.('Marketing Form Success', {
    emailAddress: values?.email ?? '',
    formName: form,
    host: process.env.GATSBY_HOST ?? '',
  });

  setWebExUserAttributes({
    hasSubmittedForm: true,
    [`${form}FormComplete`]: true,
  });

  if (form === 'demo') {
    sendWebexEvent('global_conversion_get_a_demo_eng');
  } else if (form === 'signup') {
    sendWebexEvent('global_conversion_sign_up_eng');
    Sentry.captureMessage('Signup form success called', "info");

    // Track successful signup submission
    trackSignupSubmission(window.location.href);

    // Identify user in HubSpot
    if (values?.email && typeof window !== 'undefined' && window._hsq) {
      try {
        const hubspotProperties: { email: string; [key: string]: string | number | boolean; } = {
          email: values.email as string,
        };

        // Add standard contact properties
        if (values.company) {
          hubspotProperties.company = values.company as string;
        }

        if (values.company_website) {
          hubspotProperties.website = values.company_website as string;
        }

        if (values.phone) {
          hubspotProperties.phone = values.phone as string;
        }

        // field_16: How will you be using Klaviyo? (own business vs client's business)
        if (values.field_16) {
          hubspotProperties.how_will_you_be_using_klaviyo_ = values.field_16 as string;
        }

        // field_1: Marketing communications opt-in
        if (values.field_1 !== undefined) {
          hubspotProperties.form_opt_in = values.field_1 as boolean;
        }

        // demo_requested: User requested live consultation
        if (values.demo_requested !== undefined) {
          hubspotProperties.demo_requested = values.demo_requested as boolean;
        }

        // Language and timezone preferences
        if (values.preferred_language) {
          hubspotProperties.preferred_language = values.preferred_language as string;
        }

        if (values.preferred_timezone) {
          hubspotProperties.preferred_timezone = values.preferred_timezone as string;
        }

        if (values.utm_medium) {
          hubspotProperties.utm_medium = values.utm_medium as string;
        }

        if( values.utm_term) {
          hubspotProperties.utm_term = values.utm_term as string;
        }

        if( values.utm_content) {
          hubspotProperties.utm_content = values.utm_content as string;
        }

        if( values.utm_campaign) {
          hubspotProperties.utm_campaign = values.utm_campaign as string;
        }

        if( values.utm_source) {
          hubspotProperties.utm_source = values.utm_source as string;
        }

        if( values.gclid) {
          hubspotProperties.gclid = values.gclid as string;
        }
        
        if (hasStorageAvailable('sessionStorage')) {
          const klaviyoPagesVisitCount = sessionStorage.getItem('klaviyoPagesVisitCount');
          if (klaviyoPagesVisitCount) {
            try {
              const pagesArray = JSON.parse(klaviyoPagesVisitCount);
              hubspotProperties.web_pages_visited_prior_to_conversion = JSON.stringify(pagesArray);
            } catch (error) {
              console.error('Error parsing klaviyoPagesVisitCount:', error);
            }
          }
        }

        window._hsq.push(['identify', hubspotProperties]);
        window._hsq.push(['trackPageView']);
        Sentry.captureMessage('Signup form success completed, HubSpot identify called', "info");
      } catch (error) {
        console.error('Error calling HubSpot identify:', error);
        Sentry.captureException(error, {
          tags: {
            error_type: 'hubspot_identify',
          }
        });
      }
    }
  }
};

export default handleSuccess;
